import { Client } from "@/Common/Api/Client";
import { ClientResponse } from "@/Common/Api/ClientResponse";
import { DSQConflitingMedication } from "@/Common/Api/Dsq/Dto/Responses/DSQConflictingMedications";
import { IPharmacyInfo } from "@/Common/Api/Pharmacy/Dto/Responses/IPharmacyInfo";
import { VigilanceAlertsResponse } from "@/Common/Api/VigilanceSante/Dto/VigilanceAlertsResponse";
import { VigilanceQueryViewerResponse } from "@/Common/Api/VigilanceSante/Dto/VigilanceQueryViewerResponse";
import { VigilanceTokenClient } from "@/Common/Api/VigilanceSante/VigilanceTokenClient";
import { AdditionalSignOffInformation } from "@/Common/Components/Modals/LinkingPopup/LinkingPopup";
import { PdfPageDetails } from "@/Common/Components/Pdf/PdfViewer/PdfViewer";
import { MyleHelper } from "@/Common/Helpers/MyleHelper";
import { Guid } from "@/Common/Models/Guid";
import { MyleTabKey } from "@/Common/Models/MyleMenu/enums/MyleTabKey";
import { PrescriptionRenewalsCounters } from "@/Common/Models/MyleMenu/Tooltip/PrescriptionRenewalsCounters";
import { OntarioPatientEnrollmentModel } from "@/Common/Models/Patient/PatientEnrollment/Ontario/OntarioPatientEnrollmentModel";
import { SortDirection } from "@/Common/Models/UI/Enums";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { BillingProvider, BillingProviderHelper } from "@/Common/Services/ExternalBilling/BillingProvider";
import { ExternalBillingManager } from "@/Common/Services/ExternalBilling/ExternalBillingManager";
import { DirectoryPharmacy } from "@/ContactDirectory/Models/DirectoryPharmacy";
import { LoadMedicalDocuments } from "@/Documents/Api/Dto/Response/LoadMedicalDocuments";
import { InboundFaxDetails } from "@/Documents/Models/InboundFax/InboundFaxDetails";
import { MedicalDocumentItemDto } from "@/Documents/Models/Medical/MedicalDocumentItem";
import { IDocumentInformation } from "@/Documents/Models/UI/IDocumentCreateEditUIModel";
import { IFaxCoverSheetProps } from "@/ExportableFile/Components/IFaxInformationProps";
import { IExportableDto } from "@/ExportableFile/Models/ExportableFile";
import { UserSettings } from "@/Globals/Models/UserSettings";
import { MedicalItemMedicationAndPrescription } from "@/Patient/Models/MedicalSummary/MedicalItemMedicationAndPrescription";
import { MedicationsOrigin, MedicationsStatus } from "@/Patient/Models/UI/MedicalNote/Enums/MedicalItem";
import { PrescriptionRenewalHistoryOrderType } from "../Common/PrescriptionRenewalHistoryOrderType";
import { PrescriptionRenewalCurrentDocument } from "../Models/PrescriptionRenewalCurrentDocument";
import { PrescriptionRenewalFilterSettingModel } from "../Models/PrescriptionRenewalFilterSettingModel";
import { PrescriptionRenewalMedications } from "../Models/PrescriptionRenewalMedications";
import { PrescriptionRenewalSettings } from "../Models/Settings/PrescriptionRenewalSettings";
import { ActiveMedicationPdfResponse } from "./Dto/ActiveMedicationPdfResponse";
import { CreateEncounterFaxAndPrescribeResponse } from "./Dto/CreateEncounterFaxAndPrescribeResponse";
import { DsqMedicationsResponse } from "./Dto/DsqMedicationsResponse";
import { GetCumulativePatientProfileByPrescriptionRenewalResponse } from "./Dto/GetCumulativePatientProfileByPrescriptionRenewalResponse";
import { GetPrescriptionRenewalByIdResponse } from "./Dto/GetPrescriptionRenewalByIdResponse";
import { PrescriptionRenewalMedicationsResponse } from "./Dto/PrescriptionRenewalMedicationsResponse";
import { PrescriptionRenewalRequest } from "./Dto/PrescriptionRenewalRequest";
import { PrescriptionRenewalResponse } from "./Dto/PrescriptionRenewalResponse";
import { PrescriptionRenewalsCountResponse } from "./Dto/PrescriptionRenewalsCountResponse";

interface IFetchMedicationHistoryPDFRequest {
    PatientContactId: string;
    FromDate: Date;
    ToDate: Date;
    Statuses: MedicationsStatus[];
    Origins: MedicationsOrigin[];
    SortColumn: PrescriptionRenewalHistoryOrderType;
    SortDirection: SortDirection;
    SearchText: string;
    SearchRenewalFields: boolean;
}

const fetchPrescriptionsRenewalCounters = async (): Promise<void> => {
    const prescriptionRenewalRequestData = {
        ResponsibleProviderContactId: sessionStore.individualContactId
    };
    const prescriptionRenewalCounters = await Client.postAsync<PrescriptionRenewalsCountResponse>(ServerOperations.Server.Myle_PrescriptionRenewal_CountPrescriptionRenewal, prescriptionRenewalRequestData);

    if (prescriptionRenewalCounters.isSuccess) {
        const prescriptionRenewalsCounters = new PrescriptionRenewalsCounters(
            prescriptionRenewalCounters.data.NewPrescriptionRenewalsCount,
            prescriptionRenewalCounters.data.UrgentNewPrescriptionRenewalsCount,
            prescriptionRenewalCounters.data.PendingPrescriptionRenewalsCount,
            prescriptionRenewalCounters.data.UrgentPendingPrescriptionRenewalsCount
        );

        cacheStore.UpdateTab(
            MyleTabKey.PrescriptionRenewals,
            prescriptionRenewalCounters.data.NewPrescriptionRenewalsCount,
            prescriptionRenewalCounters.data.UrgentNewPrescriptionRenewalsCount,
            prescriptionRenewalCounters.data.PendingPrescriptionRenewalsCount,
            prescriptionRenewalCounters.data.UrgentPendingPrescriptionRenewalsCount,
            true,
            { prescriptionRenewalsCounters }
        );

        cacheStore.EmitChangeToListeners();
    }
};

const fetchPrescriptionsRenewals = async (prescriptionRenewalRequestData: PrescriptionRenewalRequest): Promise<PrescriptionRenewalResponse> => {
    const response = await Client.postAsync<PrescriptionRenewalResponse>(ServerOperations.Server.Myle_GetPrescriptionRenewals, prescriptionRenewalRequestData);
    return response.data;
};

const fetchPrescriptionsRenewalMedications = async (prescriptionRenewalId: Guid | string): Promise<PrescriptionRenewalMedicationsResponse> => {
    const response = await Client.postAsync<PrescriptionRenewalMedicationsResponse>(ServerOperations.Server.Myle_PrescriptionRenewal_GetPrescriptionRenewalMedications, {
        PrescriptionRenewalId: prescriptionRenewalId
    });

    return response.data;
};

/**
 * Load document and prescription renewal details by PrescriptionRenewal's Id
 * @async
 * @function fetchPrescriptionRenewalById
 * @param prescriptionRenewalId - The PrescriptionRenewalId to fetch the document Id, and prescription renewal details
 * @return The PrescriptionRenewal's details and document
 */
async function fetchPrescriptionRenewalById(prescriptionRenewalId: Guid | string): Promise<GetPrescriptionRenewalByIdResponse> {
    const response = await Client.postAsync<GetPrescriptionRenewalByIdResponse>(ServerOperations.Server.Myle_GetPrescriptionRenewalById, {
        PrescriptionRenewalId: prescriptionRenewalId
    });

    return response.data;
}

const fetchMedicationHistoryPDF = async (requestData: IFetchMedicationHistoryPDFRequest): Promise<ActiveMedicationPdfResponse> => {
    const response = await Client.postAsync<ActiveMedicationPdfResponse>(ServerOperations.Server.Myle_Patient_Medication_GeneratePDF, requestData);
    return response.data;
};

const savePrescriptionRenewalFilterSetting = async (filterSettings: PrescriptionRenewalFilterSettingModel): Promise<void> => {
    const userSettings = new UserSettings();
    userSettings.PrescriptionRenewalFilterSettings = PrescriptionRenewalFilterSettingModel.toCacheModel(filterSettings);

    const requestData = {
        UserSettings: userSettings,
        UserId: sessionStore.userId
    };

    await Client.postAsync<PrescriptionRenewalSettings>(ServerOperations.Server.Myle_UpdateUserSettings, requestData);
};

const createNewPrescriptionRenewalClaim = async (prescriptionRenewalId: Guid | string, billingProvider: BillingProvider): Promise<void> => {
    if (!BillingProviderHelper.getBillingProviderSupportsPrescriptionRenewal(billingProvider))
        return;

    await ExternalBillingManager.openCreatePrescriptionClaimWindowAsync(
        prescriptionRenewalId,
        billingProvider,
        await sessionStore.getCurrentEmployee().getExternalBillingUserAsync()
    );
};

const createNewMedfarClaim = async (): Promise<void> => {
    // TODO: Need to update this method on RAMQ Subscription
    const licence = sessionStore.getCurrentEmployee().Model.FirstLicenceNumber;
    if (IsNullOrWhiteSpace(licence)) {
        sessionStore.showPopup({
            title: cacheStore.GetString("LicenceErrorLabel"),
            content: [cacheStore.GetString("LicenceErrorMessageLabel"), `Licence : ${licence || "--"}`]
        });
        return;
    }

    const response = await Client.postAsync<{ HasSpecialtyCode: boolean, IsSpecialist: boolean }>(
        ServerOperations.Server.MedfarBilling_CheckIfHasSpecialtyCode,
        { Licence: licence }
    );

    if (response.isSuccess) {
        // TODO: Need to open the Claim Popup
    }
};

const fetchMedicalDocumentData = async (documentId: Guid): Promise<MedicalDocumentItemDto> => {
    const documentResult = await Client.postAsync<LoadMedicalDocuments>(ServerOperations.Server.Myle_LoadDocument, { DocumentIds: [documentId] });
    return documentResult.data.MedicalDocumentListItem[0];
};

const fetchVigilanceAlerts = async (patientContactId: string, newDINs: string[], interuptedDINs: string[], eventId: string): Promise<VigilanceAlertsResponse> => {
    const token = await VigilanceTokenClient.getVigilanceTokenAsync();
    const response = await Client.postAsync<VigilanceAlertsResponse>(ServerOperations.Server.MedfarDictionnary_GetVigilanceNumberOfAlerts, {
        individualId: patientContactId,
        DINs: newDINs,
        ExcludeDINs: interuptedDINs,
        EventId: eventId,
        VigilanceToken: token
    });

    return response.data;
};

const fetchVigilanceQueryViewerData = async (patientContactId: string, newDrugIdNumbers: string[], interuptedDrugIdNumbers: string[]): Promise<VigilanceQueryViewerResponse> => {
    const token = await VigilanceTokenClient.getVigilanceTokenAsync();
    const response = await Client.postAsync<VigilanceQueryViewerResponse>(ServerOperations.Server.MedfarDictionary_GetVigilanceQueryViewerData, {
        PatientContactId: patientContactId,
        DrugIdNumbers: newDrugIdNumbers,
        ExcludeDrugIdNumbers: interuptedDrugIdNumbers,
        VigilanceToken: token
    });

    return response.data;
};

const savePrescriptionRenewal = async (
    prescriptionRenewalId: Guid | string,
    patientId: Guid,
    note: string,
    medicalItemsMedicationAndPrescription: MedicalItemMedicationAndPrescription[],
    prescriptionRenewalMedications: PrescriptionRenewalMedications[]
): Promise<void> => {
    const response = await Client.postAsync(
        ServerOperations.Server.Myle_SavePrescriptionRenewalMedications,
        {
            PrescriptionRenewalId: prescriptionRenewalId,
            PatientContactId: patientId,
            Note: note,
            MedicalItemsMedicationAndPrescriptions: medicalItemsMedicationAndPrescription,
            PrescriptionRenewalMedications: prescriptionRenewalMedications
        }
    );

    return response.data;
};

const completePrescriptionRenewal = async (
    prescriptionRenewalId: Guid | string,
    patientContactFacetId: Guid | string,
    patientContactId: Guid | string,
    medicalItemsMedicationAndPrescription: MedicalItemMedicationAndPrescription[],
    documentId: Guid,
    relatedPrescriptionRenewalMedications: PrescriptionRenewalMedications[],
    prescriptionRenewalNote: string,
    isFavorite: boolean,
    isDsqRequest: boolean = false,
    conflictingMedications: DSQConflitingMedication[] = null,
    dsqOriginalMedications: MedicalItemMedicationAndPrescription[] = null
): Promise<CreateEncounterFaxAndPrescribeResponse> => {
    const request = {
        PrescriptionRenewalId: prescriptionRenewalId,
        PatientContactFacetId: patientContactFacetId,
        PatientContactId: patientContactId,
        ConsultationTypeId: sessionStore.getCurrentEmployeeDefaultSpecializationLookupId(),
        SiteId: sessionStore.getCurrentEmployee().getActiveSite().Model.Id,
        MedicalItemsMedicationAndPrescriptions: medicalItemsMedicationAndPrescription,
        DocumentId: documentId,
        PrescriptionRenewalMedications: relatedPrescriptionRenewalMedications,
        Note: prescriptionRenewalNote,
        IsFavorite: isFavorite,
        IsDsqRequest: isDsqRequest,
        ConflictingMedications: conflictingMedications,
        DsqOriginalMedications: dsqOriginalMedications
    };

    if (isDsqRequest) {
        const onSuccess = new Promise<CreateEncounterFaxAndPrescribeResponse>(resolve => {
            Client.PostDSQ(
                ServerOperations.Server.Myle_PrescriptionRenewal_CompletePrescription,
                request,
                response => resolve(response)
            );
        });
        return await onSuccess;
    }

    const response = await Client.postAsync<CreateEncounterFaxAndPrescribeResponse>(ServerOperations.Server.Myle_PrescriptionRenewal_CompletePrescription, request);
    return response?.data;
};

const faxPrescriptionRenewal = async (
    patientContactId: Guid | string,
    exportableFile: IExportableDto,
    signedExportableFile: IExportableDto,
    faxNumber: string,
    fileDescription: string,
    attachedDocument?: PrescriptionRenewalCurrentDocument,
    mylePharmacy?: DirectoryPharmacy,
    faxCoverSheet?: IFaxCoverSheetProps,
    recipientPhoneNumber?: string
): Promise<ClientResponse> => {
    const response = await Client.postAsync(
        ServerOperations.Server.Myle_Faxing_SendExportableFileFax,
        {
            RecipientPhoneNumber: recipientPhoneNumber,
            PatientContactId: patientContactId,
            ExportableFileDtoSigned: signedExportableFile,
            ExportableFileDto: exportableFile,
            FaxNumber: faxNumber,
            FaxNumberDescription: fileDescription,
            DocumentId: attachedDocument?.documentId,
            DocumentByteId: attachedDocument?.documentByteId,
            MylePharmacy: mylePharmacy?.isMylePharmacy
                ? {
                    ClientTag: mylePharmacy.mylePharmacyClientTag,
                    Banner: mylePharmacy.banner,
                    Pharmacists: mylePharmacy.pharmacists,
                    Address: {
                        StreetAddress: mylePharmacy.address.streetAddress,
                        City: mylePharmacy.address.city,
                        PostalCode: mylePharmacy.address.postalCode,
                        StateId: mylePharmacy.address.stateId,
                        CountryId: mylePharmacy.address.countryId
                    }
                }
                : null,
            PdfCoverPageParams: faxCoverSheet
        }
    );
    return response;
};

const signoffEncounter = async (encounterId: Guid | string, additionalSignOffInformation: AdditionalSignOffInformation): Promise<ClientResponse> => {
    const response = await Client.postAsync(
        ServerOperations.Server.Myle_Patient_MedicalNote_SignoffEncounter,
        {
            EncounterId: encounterId,
            appointmentId: additionalSignOffInformation.appointmentId,
            InterfaceNoteCategory: additionalSignOffInformation.interfaceNoteCategory,
            ConsultationTypeId: sessionStore.getCurrentEmployeeDefaultSpecializationLookupId(),
            IsImplicitlyApproved: true
        }
    );

    additionalSignOffInformation?.ValidationCallback?.(response.isSuccess);

    return response;
};

const fetchDsqMedicationSummary = (patientContactId: string, onSuccess: (success: boolean, dsqMedicationsResponse: DsqMedicationsResponse) => void): void => {
    const requestData = {
        PatientContactId: patientContactId,
        ActiveMedicationOnly: true
    };

    Client.PostDSQ(
        ServerOperations.Server.DSQ_MedicationSummary,
        requestData,
        response => onSuccess(!response.Errors?.length, response)
    );
};

/**
 * Generate and pop out a pdf by page details
 * @param documentByteId The Id of document byte that contains page details
 * @param pageDetails The fetched page information
 */
const onPdfPopoutClick = async (documentByteId: Guid | string, pageDetails: PdfPageDetails[]): Promise<void> => {
    function generatePageConfiguration(): { pageNumber: number, pageOrientation: number }[] {
        return pageDetails.map(p => ({
            pageNumber: p.pageNumber,
            pageOrientation: p.orientation
        }));
    }

    const pageOrientation = generatePageConfiguration();
    const queryString = pageOrientation.map(p => `${p.pageNumber};${p.pageOrientation}`).join(",");
    const docPath = apiSettings.buildAppUrl(`api/medicalDocuments/enlargeSelectedPages/${documentByteId}?pageOrientation=${queryString}`, { auth: true });
    MyleHelper.OpenNewWindow(docPath);
};

const fetchCumulativePatientProfile = async (prescriptionRenewalId: Guid | string): Promise<GetCumulativePatientProfileByPrescriptionRenewalResponse> => {
    const response = await Client.postAsync<GetCumulativePatientProfileByPrescriptionRenewalResponse>(ServerOperations.Server.Myle_GetCumulativePatientProfileByPrescriptionRenewal, {
        PrescriptionRenewalId: prescriptionRenewalId
    });

    if (!response.isSuccess) {
        return null;
    }

    return response.data;
};

const setPreferredPharmacy = async (patientContactId: Guid | string, pharmacy: IPharmacyInfo): Promise<void> => {
    await Client.postAsync(ServerOperations.Server.Myle_UpdatePreferredPharmacy, {
        PatientContactId: patientContactId,
        PharmacyName: pharmacy.Name,
        PharmacyBanner: pharmacy.Banner,
        PharmacyPhone: pharmacy.Phone,
        PharmacyFax: pharmacy.Fax,
        PharmacyAddress: pharmacy.Address
    });
};

/**
 * Return document with comment
 * @async
 * @param faxDetails - The details of an inbound fax, including cover sheet parameters.
 * @param patientContactFacetId - The ID of the patient contact facet.
 * @param documentId - The ID of the document to add comments to.
 * @param documentByteId - The ID of the document byte to add comments to.
 */
const returnDocumentWithComment = async (
    faxDetails: InboundFaxDetails,
    patientContactFacetId: Guid | string,
    documentId: Guid,
    documentByteId: Guid
): Promise<ClientResponse> => {
    const { faxNumber, notes, phoneNumber, displayName, pdfCoverPageParams } = faxDetails;
    const response = await Client.postAsync(ServerOperations.Server.Myle_Faxing_ReturnDocumentWithComment, {
        FaxNumber: faxNumber,
        Notes: notes,
        RecipientPhoneNumber: phoneNumber,
        RecipientName: displayName,
        PdfCoverPageParams: pdfCoverPageParams,
        PatientContactFacetId: patientContactFacetId,
        DocumentId: documentId,
        DocumentByteId: documentByteId
    });

    return response;
};

/**
 * Update document information
 * @async
 * @param documentData - document information to update.
 */
const updateDocumentInformation = async (documentData: IDocumentInformation): Promise<boolean> => {
    const response = await Client.postAsync(ServerOperations.Server.Myle_UpdateApprouvedDocumentMetadataRequestResponse, {
        Id: documentData.referenceId,
        Note: documentData.note,
        Source: documentData.source,
        ReportClassId: documentData.docClass,
        ReportSubClassId: documentData.docSubClass,
        Subject: documentData.description,
        ResponsibleProvidersIds: documentData.responsibleProvidersIds,
        ReceiptDate: MyleHelper.FormatDate(documentData.date),
        IsApproved: documentData.isApproved,
        PrescriptionRenewalId: documentData.prescriptionRenewalId
    });

    return response.isSuccess;
};

/**
 * Fetch a patient enrollment model of Ontario by a contactFacetId
 * @returns it returns a patient enrollment model in Ontario
 */
const fetchOntarioPatientEnrollmentModel = async (patientContactFacetId: string): Promise<OntarioPatientEnrollmentModel | null> => {
    const result = await Client.postAsync<{ PatientEnrollment: OntarioPatientEnrollmentModel }>(
        ServerOperations.Server.Myle_OntarioPatientEnrollment_GetEnrollmentForStatus,
        { PatientId: patientContactFacetId }
    );

    if (!result.isSuccess) {
        return null;
    }

    return result.data?.PatientEnrollment;
};

export {
    completePrescriptionRenewal,
    createNewMedfarClaim,
    createNewPrescriptionRenewalClaim,
    faxPrescriptionRenewal,
    fetchCumulativePatientProfile,
    fetchDsqMedicationSummary,
    fetchMedicalDocumentData,
    fetchMedicationHistoryPDF,
    fetchOntarioPatientEnrollmentModel,
    fetchPrescriptionRenewalById,
    fetchPrescriptionsRenewalCounters,
    fetchPrescriptionsRenewalMedications,
    fetchPrescriptionsRenewals,
    fetchVigilanceAlerts,
    fetchVigilanceQueryViewerData,
    onPdfPopoutClick,
    returnDocumentWithComment,
    savePrescriptionRenewal,
    savePrescriptionRenewalFilterSetting,
    setPreferredPharmacy,
    signoffEncounter,
    updateDocumentInformation
};
